import React, { useMemo, useRef, useState } from 'react';
import { Avatar } from '../../../../components/Portal/Work/Avatar';
import {
	PRIVATE_NOTE_TAGS,
	tagByKey,
	knownTags,
	filterEntries,
	filterLine,
	countLabel,
	whenLine,
	buildEntry,
	mentionQueryAt,
	insertMention,
	MAX_PRIVATE_NOTE_LENGTH,
} from '../../../../services/privateNotesModel';
import {
	MENTIONABLE_STAFF,
	STAFF_UIDS,
	addPrivateNote,
	editPrivateNote,
	deletePrivateNote,
} from '../../../../services/PrivateNotesService';
import './PrivateFile.css';

/**
 * The Private file (design pass 4, Part A; built 2026-10-04). What the teachers
 * need to know about a student and the family must never see. Lives in the
 * lesson's left rail, and in a sheet wherever the rail is hidden or too narrow:
 * SAME component, `variant` only changes the header's right-hand control.
 *
 * AT REST: Add a private note, the two newest entries in full, and a History
 * row. HISTORY: the eight tag filters, a count line, every entry. Collapse
 * returns to rest. The filter is per lesson session (component state only).
 *
 * The visual grammar is the design's rule, not decoration: PRIVATE = lock +
 * ink (the heading colour), never teal; private entries never look like Song
 * Note cards. Every teacher-visible failure leads with "Please tell Anthony
 * right away." (feedback memory), detail small underneath.
 *
 * MENTIONS are derived from the TEXT at save time: a mention exists exactly
 * when "@First" is in the words, so deleting the word un-mentions and typing it
 * by hand mentions. The "@" popover and the Mention button are helpers that
 * insert the word; they hold no state of their own that could disagree with it.
 */

const mentionsInText = (text, authorId) =>
	MENTIONABLE_STAFF.filter(
		(p) => p.uid !== authorId && new RegExp(`(^|\\s)@${p.firstName}\\b`, 'i').test(text || '')
	).map((p) => p.uid);

/** Render "@Grace" as a marked mention; everything else as plain text. */
const EntryText = ({ text }) => {
	const names = MENTIONABLE_STAFF.map((p) => p.firstName).join('|');
	if (!names) return <p className="pf-entry__text">{text}</p>;
	const parts = String(text || '').split(new RegExp(`(@(?:${names})\\b)`, 'gi'));
	return (
		<p className="pf-entry__text">
			{parts.map((part, i) =>
				/^@/.test(part) && i % 2 === 1 ? (
					<span key={i} className="pf-mention">
						<i className="fa-sharp fa-solid fa-at" aria-hidden="true" />
						{part.slice(1)}
					</span>
				) : (
					<React.Fragment key={i}>{part}</React.Fragment>
				)
			)}
		</p>
	);
};

const TagChip = ({ tagKey, on = false, big = false, onClick = null, label = null, icon = null }) => {
	const tag = tagByKey(tagKey);
	const text = label || tag?.label || tagKey;
	const glyph = on && onClick ? 'fa-check' : icon || tag?.icon || 'fa-tag';
	const cls = `pf-tag${on ? ' is-on' : ''}${big ? ' pf-tag--big' : ''}`;
	if (!onClick) {
		return (
			<span className={cls}>
				<i className={`fa-sharp fa-solid ${glyph}`} aria-hidden="true" />
				{text}
			</span>
		);
	}
	return (
		<button type="button" className={cls} aria-pressed={on} onClick={onClick}>
			<i className={`fa-sharp fa-solid ${glyph}`} aria-hidden="true" />
			{text}
		</button>
	);
};

const TagPicker = ({ value, onChange }) => (
	<div className="pf-tags" role="group" aria-label="Topic tags">
		{PRIVATE_NOTE_TAGS.map((t) => {
			const on = value.includes(t.key);
			return (
				<TagChip
					key={t.key}
					tagKey={t.key}
					on={on}
					onClick={() => onChange(on ? value.filter((k) => k !== t.key) : [...value, t.key])}
				/>
			);
		})}
	</div>
);

const FailLine = ({ detail, onDismiss = null }) => (
	<div className="pf-fail" role="alert">
		<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" />
		<span>
			<strong>Please tell Anthony right away.</strong>
			<span className="pf-fail__detail">{detail}</span>
		</span>
		{onDismiss && (
			<button type="button" className="pf-iconbtn" aria-label="Dismiss" onClick={onDismiss}>
				<i className="fa-sharp fa-solid fa-xmark" aria-hidden="true" />
			</button>
		)}
	</div>
);

/** The composer: ink band, words, topic tags, Save / Mention / Cancel. */
const Composer = ({ studentId, lessonId, currentUid, authorName, onDone }) => {
	const [text, setText] = useState('');
	const [tags, setTags] = useState([]);
	const [mentionQuery, setMentionQuery] = useState(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const taRef = useRef(null);

	const candidates = useMemo(
		() =>
			mentionQuery === null
				? []
				: MENTIONABLE_STAFF.filter(
						(p) => p.uid !== currentUid && p.firstName.toLowerCase().startsWith(mentionQuery.toLowerCase())
				  ),
		[mentionQuery, currentUid]
	);

	const syncQuery = (value, caret) => setMentionQuery(mentionQueryAt(value, caret));

	const pick = (person) => {
		const ta = taRef.current;
		const caret = ta ? ta.selectionStart : text.length;
		const next = insertMention(text, caret, person.firstName);
		setText(next.text);
		setMentionQuery(null);
		requestAnimationFrame(() => {
			if (taRef.current) {
				taRef.current.focus();
				taRef.current.setSelectionRange(next.caret, next.caret);
			}
		});
	};

	const startMention = () => {
		const ta = taRef.current;
		const caret = ta ? ta.selectionStart : text.length;
		const before = text.slice(0, caret);
		const insert = before && !/\s$/.test(before) ? ' @' : '@';
		const next = before + insert + text.slice(caret);
		setText(next);
		setMentionQuery('');
		requestAnimationFrame(() => {
			if (taRef.current) {
				const pos = caret + insert.length;
				taRef.current.focus();
				taRef.current.setSelectionRange(pos, pos);
			}
		});
	};

	const save = async () => {
		if (saving) return;
		const built = buildEntry({
			text,
			tags,
			mentions: mentionsInText(text, currentUid),
			authorId: currentUid,
			authorName,
			lessonId,
			staffUids: STAFF_UIDS,
		});
		if (built.error) {
			setError(built.error);
			return;
		}
		setSaving(true);
		setError('');
		try {
			await addPrivateNote(studentId, built.entry);
			onDone();
		} catch (err) {
			console.error('Saving a private note failed:', err);
			setError(`The private note did not save: ${err?.message || err}`);
			setSaving(false);
		}
	};

	const canSave = text.trim().length > 0 && !saving;
	const validationOnly = error && !/did not save/.test(error);

	return (
		<div className="pf-composer">
			<div className="pf-composer__band">
				<i className="fa-sharp fa-solid fa-lock" aria-hidden="true" />
				Private note · teachers only
				<small>never sent</small>
			</div>
			<div className="pf-composer__field">
				<label className="pf-visually-hidden" htmlFor="pf-composer-text">
					Private note
				</label>
				<textarea
					id="pf-composer-text"
					ref={taRef}
					className="pf-composer__text"
					value={text}
					maxLength={MAX_PRIVATE_NOTE_LENGTH}
					autoFocus
					rows={4}
					placeholder="What should the next teacher know? Type @ to mention someone."
					onChange={(e) => {
						setText(e.target.value);
						syncQuery(e.target.value, e.target.selectionStart);
					}}
					onKeyUp={(e) => syncQuery(e.currentTarget.value, e.currentTarget.selectionStart)}
					onClick={(e) => syncQuery(e.currentTarget.value, e.currentTarget.selectionStart)}
					onKeyDown={(e) => {
						if (e.key === 'Escape' && mentionQuery !== null) {
							e.stopPropagation();
							setMentionQuery(null);
						}
						if (e.key === 'Enter' && mentionQuery !== null && candidates.length) {
							e.preventDefault();
							pick(candidates[0]);
						}
					}}
				/>
				{mentionQuery !== null && candidates.length > 0 && (
					<ul className="pf-mentionpop" role="listbox" aria-label="Mention a teacher">
						{candidates.map((p) => (
							<li key={p.uid}>
								<button
									type="button"
									role="option"
									aria-selected="false"
									className="pf-mentionpop__row"
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => pick(p)}>
									<Avatar uid={p.uid} name={p.name} size={28} />
									<span className="pf-mentionpop__name">{p.firstName}</span>
									<small>notified in the portal</small>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
			<div className="pf-composer__tags">
				<span className="pf-eyebrow">Topic tags</span>
				<TagPicker value={tags} onChange={setTags} />
			</div>
			{error && (validationOnly ? <p className="pf-hint">{error}</p> : <FailLine detail={error} />)}
			<div className="pf-composer__acts">
				<button
					type="button"
					className="pf-btn pf-btn--ink"
					aria-disabled={!canSave}
					onClick={() => {
						if (!canSave) return;
						save();
					}}>
					<i className="fa-sharp fa-solid fa-lock" aria-hidden="true" />
					{saving ? 'Saving…' : 'Save private note'}
				</button>
				<button type="button" className="pf-btn" onClick={startMention}>
					<i className="fa-sharp fa-solid fa-at" aria-hidden="true" />
					Mention
				</button>
				<button type="button" className="pf-btn" onClick={onDone} disabled={saving}>
					Cancel
				</button>
			</div>
		</div>
	);
};

/** One entry. Admin / owner see pencil + trash; edit and delete are inline. */
const Entry = ({ entry, studentId, currentUid, lessonId, canManage, isNewToday }) => {
	const [mode, setMode] = useState('view'); // view | edit | confirm-delete
	const [draft, setDraft] = useState(entry.text || '');
	const [draftTags, setDraftTags] = useState(knownTags(entry.tags));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const mine = entry.authorId && entry.authorId === currentUid;
	const name = mine ? 'You' : entry.authorName || 'A teacher';
	const tags = knownTags(entry.tags);

	const run = async (fn, failWords) => {
		setBusy(true);
		setError('');
		try {
			await fn();
			setMode('view');
		} catch (err) {
			console.error(failWords, err);
			setError(`${failWords}: ${err?.message || err}`);
		} finally {
			setBusy(false);
		}
	};

	return (
		<article className={`pf-entry${isNewToday ? ' pf-entry--today' : ''}`}>
			<header className="pf-entry__head">
				<Avatar uid={entry.authorId} name={entry.authorName || 'Teacher'} size={28} />
				<b className="pf-entry__who">{name}</b>
				<small className="pf-entry__when">· {whenLine(entry, { currentLessonId: lessonId })}</small>
				{canManage && mode === 'view' && (
					<span className="pf-entry__acts">
						<button
							type="button"
							className="pf-iconbtn"
							aria-label="Edit this private note"
							onClick={() => {
								setDraft(entry.text || '');
								setDraftTags(knownTags(entry.tags));
								setMode('edit');
							}}>
							<i className="fa-sharp fa-solid fa-pen" aria-hidden="true" />
						</button>
						<button
							type="button"
							className="pf-iconbtn"
							aria-label="Delete this private note"
							onClick={() => setMode('confirm-delete')}>
							<i className="fa-sharp fa-solid fa-trash" aria-hidden="true" />
						</button>
					</span>
				)}
			</header>

			{mode === 'edit' ? (
				<div className="pf-edit">
					<label className="pf-visually-hidden" htmlFor={`pf-edit-${entry.id}`}>
						Edit private note
					</label>
					<textarea
						id={`pf-edit-${entry.id}`}
						className="pf-composer__text pf-edit__text"
						value={draft}
						rows={4}
						maxLength={MAX_PRIVATE_NOTE_LENGTH}
						onChange={(e) => setDraft(e.target.value)}
					/>
					<TagPicker value={draftTags} onChange={setDraftTags} />
					<div className="pf-composer__acts">
						<button
							type="button"
							className="pf-btn pf-btn--ink"
							aria-disabled={busy || !draft.trim()}
							onClick={() => {
								if (busy || !draft.trim()) return;
								run(
									() => editPrivateNote(studentId, entry.id, { text: draft, tags: draftTags }, currentUid),
									'The edit did not save'
								);
							}}>
							{busy ? 'Saving…' : 'Save edit'}
						</button>
						<button type="button" className="pf-btn" onClick={() => setMode('view')} disabled={busy}>
							Cancel
						</button>
					</div>
				</div>
			) : (
				<EntryText text={entry.text} />
			)}

			{mode === 'confirm-delete' && (
				<div className="pf-confirm" role="group" aria-label="Delete this private note?">
					<span>Delete this private note? This cannot be undone.</span>
					<div className="pf-composer__acts">
						<button
							type="button"
							className="pf-btn pf-btn--danger"
							aria-disabled={busy}
							onClick={() => {
								if (busy) return;
								run(() => deletePrivateNote(studentId, entry.id), 'The private note was not deleted');
							}}>
							<i className="fa-sharp fa-solid fa-trash" aria-hidden="true" />
							{busy ? 'Deleting…' : 'Delete'}
						</button>
						<button type="button" className="pf-btn" onClick={() => setMode('view')} disabled={busy}>
							Keep it
						</button>
					</div>
				</div>
			)}

			{entry.origin === 'recap' && (
				<span className="pf-entry__origin">
					<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
					From the Teachers only recap of that lesson
				</span>
			)}
			{mode !== 'edit' && tags.length > 0 && (
				<div className="pf-tags">
					{tags.map((k) => (
						<TagChip key={k} tagKey={k} />
					))}
				</div>
			)}
			{error && <FailLine detail={error} onDismiss={() => setError('')} />}
		</article>
	);
};

const PrivateFile = ({
	studentId,
	lessonId = null,
	entries = [],
	isLoading = false,
	error = '',
	currentUid = null,
	authorName = null,
	canManage = false,
	historyOpen = false,
	onToggleHistory,
	variant = 'rail', // 'rail' | 'sheet'
	headingId = 'pf-title',
}) => {
	const [composing, setComposing] = useState(false);
	const [filter, setFilter] = useState(null);

	const total = entries.length;
	const latest = entries.slice(0, 2);
	const shown = historyOpen ? filterEntries(entries, filter) : latest;

	const entryProps = (e) => ({
		entry: e,
		studentId,
		currentUid,
		lessonId,
		canManage,
		isNewToday: Boolean(lessonId && e.lessonId === lessonId),
	});

	return (
		<section className={`pf pf--${variant}`} aria-labelledby={headingId}>
			<header className="pf-head">
				<i className="fa-sharp fa-solid fa-lock pf-head__lock" aria-hidden="true" />
				<div className="pf-head__titles">
					{/* In the sheet, BottomSheet's own title says "Private file" and
					    owns the close X, so the header keeps only the promise. */}
					{variant !== 'sheet' && (
						<h2 id={headingId} className="pf-head__title">
							Private file
						</h2>
					)}
					<small className="pf-head__sub">Teachers only. Never goes home.</small>
				</div>
				<span className="pf-head__count">{isLoading ? '' : countLabel(total)}</span>
			</header>

			<div className="pf-body">
				{!studentId ? (
					<p className="pf-hint">Link this lesson to a student to open the private file.</p>
				) : (
					<>
						{composing ? (
							<Composer
								studentId={studentId}
								lessonId={lessonId}
								currentUid={currentUid}
								authorName={authorName}
								onDone={() => setComposing(false)}
							/>
						) : (
							<button type="button" className="pf-add" onClick={() => setComposing(true)}>
								<i className="fa-sharp fa-solid fa-lock" aria-hidden="true" />
								Add a private note
							</button>
						)}

						{error && <FailLine detail={`The private file did not load: ${error}`} />}

						{!error && !isLoading && total === 0 && (
							<p className="pf-hint">
								No private notes yet. Anything the next teacher should know, and the family should not read, goes
								here.
							</p>
						)}

						{historyOpen && total > 0 && (
							<>
								<button type="button" className="pf-row" aria-expanded="true" onClick={onToggleHistory}>
									<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
									History
									<span className="pf-row__more">
										Collapse <i className="fa-sharp fa-solid fa-chevron-up" aria-hidden="true" />
									</span>
								</button>
								<div className="pf-tags pf-filter" role="group" aria-label="Filter by topic">
									<TagChip
										tagKey="all"
										label={`All · ${total}`}
										icon="fa-list"
										big
										on={!filter}
										onClick={() => setFilter(null)}
									/>
									{PRIVATE_NOTE_TAGS.map((t) => (
										<TagChip
											key={t.key}
											tagKey={t.key}
											big
											on={filter === t.key}
											onClick={() => setFilter(filter === t.key ? null : t.key)}
										/>
									))}
								</div>
								<p className="pf-line" aria-live="polite">
									<i className="fa-sharp fa-solid fa-filter" aria-hidden="true" />
									{filterLine(shown.length, filter)}
								</p>
							</>
						)}

						{!historyOpen && total > 0 && (
							<p className="pf-line">
								<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
								Latest
							</p>
						)}

						{shown.map((e) => (
							<Entry key={e.id} {...entryProps(e)} />
						))}

						{!historyOpen && total > 0 && (
							<button type="button" className="pf-row" aria-expanded="false" onClick={onToggleHistory}>
								<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
								History · {countLabel(total)}
								<span className="pf-row__more">
									Show all <i className="fa-sharp fa-solid fa-chevron-down" aria-hidden="true" />
								</span>
							</button>
						)}
					</>
				)}
			</div>
		</section>
	);
};

export default PrivateFile;
